import { useState, useMemo } from "react";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SlidersHorizontal, Bell, Check, Loader2 } from "lucide-react";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { notificationApi, brandSubscriptionsApi } from "@/resources/notification";
import type { Notification } from "@/resources/notification/notification.api";
import {
  NOTIFICATION_TYPE_ICONS,
  NOTIFICATION_TYPE_COLORS,
  TYPE_LABELS,
  formatRelativeTime,
} from "./constants";

function getNotificationType(n: Notification): string {
  return n.data?.type ?? n.type ?? "default";
}

interface NotificationsContentProps {
  title: string;
}

export function NotificationsContent({ title }: NotificationsContentProps) {
  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<number[]>([]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    notificationApi.useInfiniteNotifications({});
  const { data: unreadCountResponse } = notificationApi.useGetUnreadCount();
  const { data: subsData } = brandSubscriptionsApi.useGetBrandSubscriptions();

  const notifications = useMemo(() => data?.pages.flatMap((p) => p.data ?? []) ?? [], [data]);
  const subscriptions = useMemo(() => subsData?.subscriptions ?? [], [subsData]);
  const brandNameById = useMemo(
    () => new Map(subscriptions.map((s) => [s.brand_id, s.brand_name])),
    [subscriptions]
  );

  const { mutate: markRead } = notificationApi.useMarkRead();
  const { mutate: markAllRead } = notificationApi.useMarkAllRead();

  const filtered = useMemo(() => {
    return notifications.filter((n) => {
      const type = getNotificationType(n);
      const notifTitle = n.data?.title ?? "";
      const body = n.data?.body ?? "";
      if (
        search &&
        !notifTitle.toLowerCase().includes(search.toLowerCase()) &&
        !body.toLowerCase().includes(search.toLowerCase())
      ) {
        return false;
      }
      if (selectedTypes.length > 0 && !selectedTypes.includes(type)) return false;
      if (selectedBrands.length > 0) {
        const brandId = n.data?.metadata?.brand_id;
        if (brandId === undefined || !selectedBrands.includes(brandId)) return false;
      }
      return true;
    });
  }, [notifications, search, selectedTypes, selectedBrands]);

  const unreadCount = unreadCountResponse?.count ?? 0;
  const hasFilters = selectedTypes.length > 0 || selectedBrands.length > 0;

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleBrand = (brandId: number) => {
    setSelectedBrands((prev) =>
      prev.includes(brandId) ? prev.filter((b) => b !== brandId) : [...prev, brandId]
    );
  };

  const handleNotificationClick = (n: Notification) => {
    if (n.read_at === null) {
      markRead(n.id);
    }
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center justify-between">
          <DialogTitle className="text-base">
            {title}
            {unreadCount > 0 && (
              <Badge variant="default" className="ml-2 text-xs">{unreadCount} new</Badge>
            )}
          </DialogTitle>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-primary hover:text-primary/80 mr-6"
              onClick={() => markAllRead()}
            >
              <Check className="h-3 w-3" /> Mark all read
            </Button>
          )}
        </div>
      </DialogHeader>

      <div className="flex items-center gap-2">
        <ClearableInput
          value={search}
          onChange={setSearch}
          placeholder="Search notifications..."
          className="bg-card"
          wrapperClassName="flex-1"
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="shrink-0 bg-card relative">
              <SlidersHorizontal className="h-4 w-4" />
              {hasFilters && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-primary" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="bg-card rounded-lg p-4 w-auto" sideOffset={10}>
            <div className="flex gap-8">
              <div className="min-w-[120px]">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 sticky top-0 bg-card">
                  BRAND
                </p>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {subscriptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No subscriptions</p>
                  ) : (
                    subscriptions.map((sub) => (
                      <label key={sub.brand_id} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={selectedBrands.includes(sub.brand_id)}
                          onCheckedChange={() => toggleBrand(sub.brand_id)}
                        />
                        <span className="text-sm">{sub.brand_name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="min-w-[120px]">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 sticky top-0 bg-card">
                  TYPE
                </p>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {Object.entries(TYPE_LABELS).map(([type, label]) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedTypes.includes(type)}
                        onCheckedChange={() => toggleType(type)}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {hasFilters && (
              <button
                onClick={() => {
                  setSelectedTypes([]);
                  setSelectedBrands([]);
                }}
                className="text-xs font-semibold text-primary hover:text-primary/80 mt-3"
              >
                Clear all
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {hasFilters && (
        <div className="flex flex-wrap gap-1.5">
          {selectedBrands.map((brandId) => {
            const sub = subscriptions.find((s) => s.brand_id === brandId);
            return (
              <Badge
                key={`brand-${brandId}`}
                variant="secondary"
                className="text-xs gap-1 cursor-pointer"
                onClick={() => toggleBrand(brandId)}
              >
                {sub?.brand_name ?? String(brandId)} ×
              </Badge>
            );
          })}
          {selectedTypes.map((t) => (
            <Badge
              key={`type-${t}`}
              variant="secondary"
              className="text-xs gap-1 cursor-pointer"
              onClick={() => toggleType(t)}
            >
              {TYPE_LABELS[t] ?? t} ×
            </Badge>
          ))}
        </div>
      )}

      <div className="max-h-[400px] overflow-y-auto -mx-6 px-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No notifications found.</p>
        ) : (
          <div className="space-y-1">
            {filtered.map((n) => {
              const type = getNotificationType(n);
              const Icon = NOTIFICATION_TYPE_ICONS[type] ?? Bell;
              const colorClass = NOTIFICATION_TYPE_COLORS[type] ?? "text-muted-foreground";
              const isUnread = n.read_at === null;
              const brandId = n.data?.metadata?.brand_id;
              const brandName = brandId !== undefined ? brandNameById.get(brandId) : undefined;
              return (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-muted ${
                    isUnread ? "bg-primary-tint-subtle" : ""
                  }`}
                >
                  <div className={`mt-0.5 ${colorClass}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">{n.data?.title}</p>
                      {isUnread && (
                        <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                      )}
                    </div>
                    {brandName && (
                      <p className="text-[11px] font-medium text-muted-foreground mt-0.5">{brandName}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">{n.data?.body}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {formatRelativeTime(n.created_at)}
                    </p>
                  </div>
                </button>
              );
            })}
            {hasNextPage && (
              <div className="flex justify-center pt-2 pb-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
